// src/pages/Apps.tsx

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { fetchAppsCatalog, CREDIT_PERIOD_LABELS, type AppInfo } from '../utils/appCatalog';
import { fetchCreditBalance } from '../utils/creditsApi';
import API_BASE_URL from '../utils/apiBase';

const Apps: React.FC = () => {
  const { token, isAdmin, isAuthenticated } = useAuth();
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [launchingAppId, setLaunchingAppId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const openApp = async (appId: string, appUrl: string) => {
    if (!token) {
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.href)}`;
      return;
    }

    setLaunchingAppId(appId);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/apps/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ app: appId }),
      });

      if (!response.ok) {
        // 402 Payment Required — the backend couldn't debit the balance.
        // Direct the user to the Credits page instead of a generic error.
        if (response.status === 402) {
          const body = await response.json().catch(() => ({}));
          setError(
            `You need ${body.required ?? ''} credits to launch this app. Head to the Credits page to top up.`,
          );
          return;
        }
        throw new Error('Unable to start session');
      }

      const data = await response.json();
      const sessionToken = data.token;
      const url = `${appUrl}?token=${encodeURIComponent(sessionToken)}`;
      window.open(url, '_blank', 'noopener,noreferrer');

      // If credits were just charged, refresh the balance display.
      if (data.launch?.charged && token) {
        const fresh = await fetchCreditBalance(token);
        setBalance(fresh);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to start app session. Please try again.');
    } finally {
      setLaunchingAppId(null);
    }
  };

  useEffect(() => {
    const loadApps = async () => {
      setLoading(true);
      const data = await fetchAppsCatalog(token);
      setApps(data);
      if (token) {
        const bal = await fetchCreditBalance(token);
        setBalance(bal);
      }
      setLoading(false);
    };

    loadApps();
  }, [token]);

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading apps...</p>
      </div>
    );
  }

  return (
    <div>
      <section className="section bg-light">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1>Snowbird Industries Apps</h1>
              <p className="text-secondary text-large">
                Tools and applications built by Snowbird Industries to help you work smarter.
              </p>
            </div>
            {isAuthenticated && balance !== null && (
              <Link
                to="/credits"
                className="card"
                style={{
                  padding: '0.85rem 1.25rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  minWidth: '160px',
                }}
                title="View credits"
              >
                <span className="text-secondary" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Credit Balance
                </span>
                <strong style={{ fontSize: '1.4rem', marginTop: '0.25rem' }}>{balance.toFixed(0)}</strong>
                <span className="text-secondary" style={{ fontSize: '0.7rem' }}>Tap to buy more</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Available Apps</h2>
          {error && (
            <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</p>
          )}
          {apps.length === 0 ? (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p className="text-secondary">No apps available at this time.</p>
            </div>
          ) : (
            <div className="grid grid-2">
              {apps.map((app) => (
                <div key={app.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0 }}>{app.name}</h3>
                      {isAdmin && app.status && app.status !== 'active' && (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            backgroundColor: app.status === 'beta' ? '#fef3c7' : '#f3f4f6',
                            color: app.status === 'beta' ? '#92400e' : '#6b7280',
                          }}
                        >
                          {app.status.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      )}
                      {app.creditCost > 0 ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.55rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: '#eef2ff',
                            color: '#4338ca',
                          }}
                          title={`${app.creditCost} credits ${CREDIT_PERIOD_LABELS[app.creditPeriod].toLowerCase()}`}
                        >
                          {app.creditCost} credits / {app.creditPeriod}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.55rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                          }}
                        >
                          Free
                        </span>
                      )}
                    </div>
                    <p className="text-secondary">{app.description}</p>
                  </div>
                  {app.image && (
                    <img
                      src={app.image}
                      alt={`${app.name} screenshot`}
                      style={{ width: '100%', maxWidth: '300px', borderRadius: '8px' }}
                    />
                  )}
                  <div>
                    <button
                      onClick={() => openApp(app.id, app.appUrl)}
                      className="btn btn-primary"
                      disabled={launchingAppId === app.id}
                    >
                      {launchingAppId === app.id ? 'Opening...' : `Open ${app.name}`}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Apps;
